// QuestionNumericPad.js
import React, { useState, useEffect } from "react";
import { QuestionTextModel, QuestionFactory, Serializer } from "survey-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";

const QUESTION_TYPE = "numericPad";
let isRegistered = false;

/* =========================================================
   QUESTION MODEL
========================================================= */
class QuestionNumericPadModel extends QuestionTextModel {
  constructor(name) {
    super(name);

    // Set default properties for numeric input
    this.inputType = "text"; // Changed from number to allow better control
    this.placeholder = "Click to enter number";
    this.autocomplete = "off";

    // Numeric pad specific properties
    this.showNumpad = true;
    this.allowDecimal = true;
    this.allowNegative = false;
    this.maxLength = 10;
    this.minValue = null;
    this.maxValue = null;

    // Custom styling
    this.numpadTheme = "light"; // light, dark, or blue
    this.showClearButton = true;
    this.showBackspaceButton = true;
  }

  getType() {
    return QUESTION_TYPE;
  }

  // Custom properties
  get showNumpad() {
    return this.getPropertyValue("showNumpad", true);
  }

  set showNumpad(val) {
    this.setPropertyValue("showNumpad", val);
  }

  get allowDecimal() {
    return this.getPropertyValue("allowDecimal", true);
  }

  set allowDecimal(val) {
    this.setPropertyValue("allowDecimal", val);
  }

  get allowNegative() {
    return this.getPropertyValue("allowNegative", false);
  }

  set allowNegative(val) {
    this.setPropertyValue("allowNegative", val);
  }

  get numpadTheme() {
    return this.getPropertyValue("numpadTheme", "light");
  }

  set numpadTheme(val) {
    this.setPropertyValue("numpadTheme", val);
  }

  get showClearButton() {
    return this.getPropertyValue("showClearButton", true);
  }

  set showClearButton(val) {
    this.setPropertyValue("showClearButton", val);
  }

  get showBackspaceButton() {
    return this.getPropertyValue("showBackspaceButton", true);
  }

  set showBackspaceButton(val) {
    this.setPropertyValue("showBackspaceButton", val);
  }

  // Validate numeric input
  validateNumericInput(value) {
    if (value === "" || value === null) return true;

    // Check if it's a valid number
    const num = parseFloat(value);
    if (isNaN(num)) return false;

    // Check decimal places
    if (!this.allowDecimal && value.includes(".")) return false;

    // Check negative
    if (!this.allowNegative && num < 0) return false;

    // Check min/max
    if (this.minValue !== null && num < this.minValue) return false;
    if (this.maxValue !== null && num > this.maxValue) return false;

    return true;
  }
}

/* =========================================================
   REACT RENDERER with Numeric Pad Modal
========================================================= */
class SurveyQuestionNumericPad extends SurveyQuestionElementBase {
  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
      inputValue: props.question.value || "",
      isAnimating: false,
    };
    this.modalRef = React.createRef();
    this.inputRef = React.createRef();
  }

  get question() {
    return this.props.question;
  }

  componentDidMount() {
    // Close modal when clicking outside
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  handleClickOutside = (event) => {
    if (
      this.modalRef.current &&
      !this.modalRef.current.contains(event.target) &&
      this.inputRef.current &&
      !this.inputRef.current.contains(event.target)
    ) {
      this.closeModal();
    }
  };

  openModal = () => {
    if (this.question.isReadOnly) return;

    this.setState({
      showModal: true,
      isAnimating: true,
      inputValue: this.question.value || "",
    });

    // Focus the input in the modal
    setTimeout(() => {
      if (this.modalRef.current) {
        const input = this.modalRef.current.querySelector("input");
        if (input) input.focus();
      }
    }, 100);
  };

  closeModal = () => {
    this.setState({ isAnimating: false });
    setTimeout(() => {
      this.setState({ showModal: false });
    }, 300);
  };

  handleInputChange = (e) => {
    const value = e.target.value;
    this.setState({ inputValue: value });
  };

  handleNumberClick = (number) => {
    let newValue = this.state.inputValue;

    if (number === "." && !this.question.allowDecimal) return;
    if (number === "-" && !this.question.allowNegative) return;

    // Handle decimal point
    if (number === ".") {
      if (!newValue.includes(".")) {
        newValue = newValue === "" ? "0." : newValue + ".";
      }
    }
    // Handle negative sign
    else if (number === "-") {
      if (newValue.startsWith("-")) {
        newValue = newValue.substring(1);
      } else {
        newValue = "-" + newValue;
      }
    }
    // Handle backspace
    else if (number === "⌫") {
      newValue = newValue.slice(0, -1);
    }
    // Handle clear
    else if (number === "C") {
      newValue = "";
    }
    // Handle numbers
    else {
      // Limit max length
      if (newValue.replace(/[^0-9]/g, "").length >= this.question.maxLength)
        return;
      newValue += number;
    }

    this.setState({ inputValue: newValue });
  };

  handleDone = () => {
    const value = this.state.inputValue;

    // Validate
    if (value === "" || this.question.validateNumericInput(value)) {
      this.question.value = value;
      this.closeModal();
    } else {
      // Show error animation
      this.setState({ isAnimating: true });
      setTimeout(() => this.setState({ isAnimating: false }), 500);
    }
  };

  getThemeStyles() {
    const theme = this.question.numpadTheme;

    const themes = {
      light: {
        modalBg: "#ffffff",
        buttonBg: "#f8f9fa",
        buttonHover: "#e9ecef",
        buttonText: "#212529",
        primary: "#007bff",
        primaryHover: "#0056b3",
        border: "#dee2e6",
        text: "#212529",
      },
      dark: {
        modalBg: "#343a40",
        buttonBg: "#495057",
        buttonHover: "#6c757d",
        buttonText: "#f8f9fa",
        primary: "#0d6efd",
        primaryHover: "#0b5ed7",
        border: "#495057",
        text: "#f8f9fa",
      },
      blue: {
        modalBg: "#e3f2fd",
        buttonBg: "#bbdefb",
        buttonHover: "#90caf9",
        buttonText: "#1565c0",
        primary: "#1976d2",
        primaryHover: "#0d47a1",
        border: "#bbdefb",
        text: "#0d47a1",
      },
    };

    return themes[theme] || themes.light;
  }

  renderNumpadModal() {
    if (!this.state.showModal) return null;

    const styles = this.getThemeStyles();
    const { inputValue, isAnimating } = this.state;
    const q = this.question;

    const numberButtons = [
      ["7", "8", "9"],
      ["4", "5", "6"],
      ["1", "2", "3"],
      [q.allowDecimal ? "." : "", "0", q.allowNegative ? "-" : ""],
    ];

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          animation: "fadeIn 0.3s ease",
        }}
      >
        <div
          ref={this.modalRef}
          style={{
            backgroundColor: styles.modalBg,
            borderRadius: "16px",
            padding: "24px",
            width: "320px",
            maxWidth: "90vw",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
            animation: `${isAnimating ? "shake" : "slideUp"} 0.3s ease`,
            border: `1px solid ${styles.border}`,
          }}
        >
          {/* Modal Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
              paddingBottom: "12px",
              borderBottom: `2px solid ${styles.border}`,
            }}
          >
            <h3
              style={{
                margin: 0,
                color: styles.text,
                fontSize: "18px",
                fontWeight: "600",
              }}
            >
              Enter Number
            </h3>
            <button
              onClick={this.closeModal}
              style={{
                background: "none",
                border: "none",
                fontSize: "24px",
                color: styles.text,
                cursor: "pointer",
                padding: "0",
                width: "30px",
                height: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) =>
                (e.target.style.backgroundColor = styles.buttonHover)
              }
              onMouseOut={(e) =>
                (e.target.style.backgroundColor = "transparent")
              }
            >
              ×
            </button>
          </div>

          {/* Display Screen */}
          <div
            style={{
              backgroundColor: styles.buttonBg,
              borderRadius: "12px",
              padding: "16px",
              marginBottom: "20px",
              border: `2px solid ${styles.border}`,
              minHeight: "60px",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              fontSize: "32px",
              fontWeight: "bold",
              color: styles.text,
              fontFamily: "'Courier New', monospace",
              overflow: "hidden",
            }}
          >
            {inputValue === "" ? (
              <span style={{ opacity: 0.5, fontSize: "20px" }}>
                {q.placeholder}
              </span>
            ) : (
              inputValue
            )}
          </div>

          {/* Action Buttons */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "10px",
              marginBottom: "20px",
            }}
          >
            {q.showClearButton && (
              <button
                onClick={() => this.handleNumberClick("C")}
                style={{
                  padding: "14px",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  gridColumn: "span 1",
                }}
                onMouseOver={(e) =>
                  (e.target.style.backgroundColor = "#c82333")
                }
                onMouseOut={(e) => (e.target.style.backgroundColor = "#dc3545")}
              >
                Clear
              </button>
            )}
            {q.showBackspaceButton && (
              <button
                onClick={() => this.handleNumberClick("⌫")}
                style={{
                  padding: "14px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  gridColumn: "span 1",
                }}
                onMouseOver={(e) =>
                  (e.target.style.backgroundColor = "#5a6268")
                }
                onMouseOut={(e) => (e.target.style.backgroundColor = "#6c757d")}
              >
                ⌫ Backspace
              </button>
            )}
          </div>

          {/* Numeric Pad */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "12px",
              marginBottom: "20px",
            }}
          >
            {numberButtons.flat().map((num, index) =>
              num ? (
                <button
                  key={index}
                  onClick={() => this.handleNumberClick(num)}
                  disabled={num === ""}
                  style={{
                    padding: "20px 0",
                    backgroundColor: styles.buttonBg,
                    border: `2px solid ${styles.border}`,
                    borderRadius: "12px",
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: styles.buttonText,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    ...(num === "" && {
                      visibility: "hidden",
                      pointerEvents: "none",
                    }),
                  }}
                  onMouseOver={(e) => {
                    if (num !== "")
                      e.target.style.backgroundColor = styles.buttonHover;
                  }}
                  onMouseOut={(e) => {
                    if (num !== "")
                      e.target.style.backgroundColor = styles.buttonBg;
                  }}
                >
                  {num}
                </button>
              ) : (
                <div key={index}></div>
              )
            )}
          </div>

          {/* Done Button */}
          <button
            onClick={this.handleDone}
            style={{
              width: "100%",
              padding: "18px",
              backgroundColor: styles.primary,
              color: "white",
              border: "none",
              borderRadius: "12px",
              fontSize: "18px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s",
              boxShadow: "0 4px 12px rgba(0, 123, 255, 0.3)",
            }}
            onMouseOver={(e) =>
              (e.target.style.backgroundColor = styles.primaryHover)
            }
            onMouseOut={(e) =>
              (e.target.style.backgroundColor = styles.primary)
            }
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  renderInputField() {
    const q = this.question;
    const value = q.value || "";
    const styles = this.getThemeStyles();

    return (
      <div style={{ position: "relative" }}>
        <input
          ref={this.inputRef}
          type="text"
          value={value}
          onClick={this.openModal}
          onChange={() => {}} // Read-only for direct typing
          placeholder={q.placeholder}
          disabled={q.isReadOnly}
          readOnly
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "14px 50px 14px 16px",
            borderRadius: "10px",
            border: `2px solid ${
              q.errors.length > 0 ? "#dc3545" : styles.border
            }`,
            backgroundColor: q.isReadOnly ? styles.buttonBg : "#ffffff",
            fontSize: "18px",
            color: styles.text,
            cursor: q.isReadOnly ? "not-allowed" : "pointer",
            transition: "all 0.2s",
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
          }}
          onFocus={this.openModal}
        />
      </div>
    );
  }

  renderElement() {
    const q = this.question;

    return (
      <div style={{ position: "relative", width: "100%" }}>
        {this.renderInputField()}
        {this.renderNumpadModal()}

        {/* Instructions */}
        <div
          style={{
            fontSize: "13px",
            color: "#6c757d",
            marginTop: "8px",
            display: "flex",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <span>• Click the field to open numeric pad</span>
          {q.allowDecimal && <span>• Decimal point allowed</span>}
          {q.allowNegative && <span>• Negative numbers allowed</span>}
          {q.minValue !== null && <span>• Min: {q.minValue}</span>}
          {q.maxValue !== null && <span>• Max: {q.maxValue}</span>}
        </div>
      </div>
    );
  }
}

export function registerNumericPadQuestion() {
  if (isRegistered) return;
  isRegistered = true;

  // Define custom properties
  Serializer.addClass(
    QUESTION_TYPE,
    [
      // Basic properties
      {
        name: "name:text",
        category: "general",
        visible: true,
        // ✅ Force text input type
        onSetValue: function (obj, value) {
          obj.name = value;
        },
      },
      {
        name: "title",
        category: "general",
        visible: true,
      },
      {
        name: "description:text",
        category: "general",
        visible: true,
      },
      {
        name: "isRequired:boolean",
        category: "general",
        visible: true,
      },
      {
        name: "readOnly:boolean",
        category: "general",
        visible: true,
      },
      {
        name: "placeholder:text",
        category: "general",
        default: "Click to enter number",
        visible: true,
      },
      // Numeric pad specific properties
      {
        name: "showNumpad:boolean",
        category: "numpad",
        default: true,
        visible: true,
      },
      {
        name: "allowDecimal:boolean",
        category: "numpad",
        default: true,
        visible: true,
      },
      {
        name: "allowNegative:boolean",
        category: "numpad",
        default: false,
        visible: true,
      },
      {
        name: "maxLength:number",
        category: "numpad",
        default: 10,
        visible: true,
      },
      // ✅ FIX: Explicitly define minValue and maxValue as numbers
      {
        name: "minValue:number",
        category: "numpad",
        visible: true,
        // ✅ Add type converter to handle string inputs
        onSetValue: function (obj, value) {
          if (value === "" || value === null || value === undefined) {
            obj.setPropertyValue("minValue", null);
          } else {
            // Convert to number
            const num = Number(value);
            obj.setPropertyValue("minValue", isNaN(num) ? null : num);
          }
        },
        onGetValue: function (obj) {
          return obj.minValue;
        },
      },
      {
        name: "maxValue:number",
        category: "numpad",
        visible: true,
        // ✅ Add type converter to handle string inputs
        onSetValue: function (obj, value) {
          if (value === "" || value === null || value === undefined) {
            obj.setPropertyValue("maxValue", null);
          } else {
            // Convert to number
            const num = Number(value);
            obj.setPropertyValue("maxValue", isNaN(num) ? null : num);
          }
        },
        onGetValue: function (obj) {
          return obj.maxValue;
        },
      },
      //   {
      //     name: "numpadTheme",
      //     category: "numpad",
      //     default: "light",
      //     choices: ["light", "dark", "blue"],
      //     visible: true,
      //   },
      {
        name: "showClearButton:boolean",
        category: "numpad",
        default: true,
        visible: true,
      },
      {
        name: "showBackspaceButton:boolean",
        category: "numpad",
        default: true,
        visible: true,
      },
    ],
    () => new QuestionNumericPadModel(""),
    "text"
  );

  // Hide unnecessary inherited properties
  const unnecessaryProps = ["inputType", "size", "maxLength", "autocomplete"];
  unnecessaryProps.forEach((propName) => {
    const prop = Serializer.findProperty(QUESTION_TYPE, propName);
    if (prop) {
      prop.visible = false;
    }
  });

  // ✅ Also fix the getters/setters in the model
  const minValueProp = Serializer.findProperty(QUESTION_TYPE, "minValue");
  const maxValueProp = Serializer.findProperty(QUESTION_TYPE, "maxValue");

  if (minValueProp) {
    minValueProp.type = "number";
    minValueProp.isLocalizable = false;
  }

  if (maxValueProp) {
    maxValueProp.type = "number";
    maxValueProp.isLocalizable = false;
  }

  // Register with QuestionFactory
  QuestionFactory.Instance.registerQuestion(
    QUESTION_TYPE,
    (name) => new QuestionNumericPadModel(name)
  );

  // Register React component
  ReactQuestionFactory.Instance.registerQuestion(QUESTION_TYPE, (props) =>
    React.createElement(SurveyQuestionNumericPad, props)
  );
}

// Auto-register
registerNumericPadQuestion();

export default QuestionNumericPadModel;
